import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getConfigurations, getStats, getEvolutionVentes, getTopProduits, getCaisseActive, ouvrirCaisse, fermerCaisse, getLoyaltyOverview, getRapportBoutique, getProduitsVendusJour } from '../services/api';
import { useConfig } from '../context/ConfigContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import {
  Store, DollarSign, TrendingUp, AlertTriangle, Package, Users,
  RefreshCw, ArrowUpRight, ArrowDownRight, ChevronRight, Plus,
  ExternalLink, Loader, ShoppingCart, Landmark, BarChart3,
  Lock, Unlock, CheckCircle, X, AlertCircle,
  Award, Gift, CreditCard, Ticket, Download, ChevronDown, ChevronUp
} from 'lucide-react';
import moment from 'moment';
import 'moment/locale/fr';
import { useAuth } from '../context/AuthContext';
import './DashboardEntreprise.css';

moment.locale('fr');

const PALETTE = ['#6366f1', '#fca311', '#10b981', '#f43f5e', '#0ea5e9', '#8b5cf6', '#f97316'];

export default function DashboardEntreprise() {
  const { user } = useAuth();
  const { loyaltyEnabled } = useConfig();
  const navigate = useNavigate();

  const [tab, setTab]               = useState('overview');
  const [timeRange, setTimeRange]   = useState('30');
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [config, setConfig]         = useState({ devise: 'XOF', nom_entreprise: '', logo: '' });
  const [dashStats, setDashStats]   = useState(null);   // /api/dashboard
  const [kpiStats, setKpiStats]     = useState(null);   // /api/dashboard/stats
  const [loyaltyOverview, setLoyaltyOverview] = useState(null); // /api/loyalty/overview

  // Onglet Rapports (ventes / dépenses par boutique sur une période)
  const [rapBoutique, setRapBoutique] = useState('all');
  const [rapFrom, setRapFrom] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [rapTo,   setRapTo]   = useState(moment().format('YYYY-MM-DD'));
  const [rapData, setRapData] = useState(null);
  const [rapLoading, setRapLoading] = useState(false);
  const [boutiques, setBoutiques]   = useState([]);
  const [alertes, setAlertes]       = useState([]);
  const [evolution, setEvolution]   = useState([]);
  const [topProduits, setTopProduits] = useState([]);
  const [produitsJour, setProduitsJour] = useState(null); // produits vendus sur la date choisie
  const [pvjDate, setPvjDate] = useState(moment().format('YYYY-MM-DD'));
  const [pvjShowAll, setPvjShowAll] = useState(false);

  // Caisses
  const [caisses,          setCaisses]          = useState({}); // { boutiqueId: caisseData|null }
  const [loadingCaisses,   setLoadingCaisses]   = useState(false);
  const [caisseModal,      setCaisseModal]      = useState(null); // { type:'ouvrir'|'fermer', boutique }
  const [fondCaisse,       setFondCaisse]       = useState('');
  const [montantCompte,    setMontantCompte]    = useState('');
  const [noteCaisse,       setNoteCaisse]       = useState('');
  const [submittingCaisse, setSubmittingCaisse] = useState(false);
  const [caissToast,       setCaisseToast]      = useState(null);

  /* ── formatters ── */
  const fmt = (n) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: config.devise || 'XOF', minimumFractionDigits: 0
  }).format(n || 0);

  const fmtK = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
    return String(Math.round(n || 0));
  };

  const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

  /* ── load ── */
  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const results = await Promise.allSettled([
        getConfigurations(),
        axios.get('/api/dashboard'),
        axios.get('/api/boutiques'),
        axios.get('/api/dashboard/alertes'),
        getEvolutionVentes(parseInt(timeRange)),
        getStats(),
        getTopProduits(parseInt(timeRange)),
        getLoyaltyOverview(),
        getProduitsVendusJour({ date: pvjDate }),
      ]);
      const [cfgRes, dashRes, boutsRes, alertRes, evoRes, kpiRes, topRes, loyRes, pvjRes] = results;

      if (cfgRes.status === 'fulfilled' && cfgRes.value.data?.data) {
        const d = cfgRes.value.data.data;
        setConfig({ devise: d.devise || 'XOF', nom_entreprise: d.nom_entreprise || '', logo: d.logo || '' });
      }
      if (dashRes.status === 'fulfilled') {
        setDashStats(dashRes.value.data);
      } else {
        console.warn('Dashboard stats indisponibles:', dashRes.reason?.response?.status, dashRes.reason?.response?.data?.message);
      }
      if (boutsRes.status === 'fulfilled') {
        const d = boutsRes.value.data;
        setBoutiques(Array.isArray(d) ? d : d?.data || []);
      }
      if (alertRes.status === 'fulfilled') {
        const d = alertRes.value.data;
        setAlertes(Array.isArray(d) ? d : []);
      }
      if (evoRes.status === 'fulfilled')  setEvolution(evoRes.value.data?.data || []);
      if (kpiRes.status === 'fulfilled')  setKpiStats(kpiRes.value.data?.data || null);
      if (topRes.status === 'fulfilled')  setTopProduits(topRes.value.data?.data || []);
      if (loyRes.status === 'fulfilled')  setLoyaltyOverview(loyRes.value.data?.data || null);
      if (pvjRes.status === 'fulfilled')  setProduitsJour(pvjRes.value.data?.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (!loading) load(true); /* eslint-disable-next-line */ }, [timeRange]);
  useEffect(() => { if (tab === 'caisses' && boutiques.length) loadCaisses(); /* eslint-disable-next-line */ }, [tab, boutiques]);
  useEffect(() => { if (tab === 'rapports') loadRapport(); /* eslint-disable-next-line */ }, [tab]);

  const loadProduitsJour = async (date) => {
    try {
      const { data } = await getProduitsVendusJour({ date });
      setProduitsJour(data.data || null);
    } catch { setProduitsJour(null); }
  };

  /* Export Excel des produits vendus sur la date choisie */
  const exportProduitsJourExcel = () => {
    if (!produitsJour?.liste?.length) return;
    const devise = config.devise || 'XOF';
    const libelleJour = moment(pvjDate).format('DD/MM/YYYY');

    const entete = [
      [`Produits vendus le ${libelleJour}`],
      [config.nom_entreprise || ''],
      [`Export du ${moment().format('DD/MM/YYYY [à] HH:mm')}`],
      [],
      ['#', 'Produit', 'Code produit', 'Quantité vendue', `Chiffre d'affaires (${devise})`],
    ];
    const lignes = produitsJour.liste.map((p, i) => ([
      i + 1,
      p.nom || 'Produit',
      p.codeProduit || '',
      p.quantiteVendue || 0,
      p.chiffreAffaires || 0,
    ]));
    const total = ['', 'TOTAL', `${produitsJour.nombreProduits || lignes.length} référence(s)`,
      produitsJour.totalQuantite || 0, produitsJour.totalCA || 0];

    const ws = XLSX.utils.aoa_to_sheet([...entete, ...lignes, [], total]);
    ws['!cols'] = [{ wch: 6 }, { wch: 40 }, { wch: 20 }, { wch: 16 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits vendus');
    XLSX.writeFile(wb, `produits-vendus-${pvjDate}.xlsx`);
  };

  const loadRapport = async () => {
    setRapLoading(true);
    try {
      const { data } = await getRapportBoutique({ boutique: rapBoutique, from: rapFrom, to: rapTo });
      setRapData(data.data || null);
    } catch {
      setRapData(null);
    } finally { setRapLoading(false); }
  };

  const loadCaisses = async () => {
    setLoadingCaisses(true);
    const result = {};
    await Promise.all(boutiques.map(async (b) => {
      try {
        const r = await getCaisseActive(b._id);
        result[b._id] = r.data.data; // null si fermée
      } catch { result[b._id] = undefined; }
    }));
    setCaisses(result);
    setLoadingCaisses(false);
  };

  const showCaisseToast = (type, text) => {
    setCaisseToast({ type, text });
    setTimeout(() => setCaisseToast(null), 3500);
  };

  const handleOuvrirCaisse = async () => {
    if (!caisseModal) return;
    setSubmittingCaisse(true);
    try {
      await ouvrirCaisse({ boutiqueId: caisseModal.boutique._id, fondCaisse: parseFloat(fondCaisse) || 0, noteOuverture: noteCaisse });
      showCaisseToast('success', `Caisse ouverte — ${caisseModal.boutique.nom}`);
      setCaisseModal(null); setFondCaisse(''); setNoteCaisse('');
      await loadCaisses();
    } catch (err) {
      showCaisseToast('error', err.response?.data?.message || err.message || 'Erreur lors de l\'ouverture');
    } finally { setSubmittingCaisse(false); }
  };

  const handleFermerCaisse = async () => {
    if (!caisseModal) return;
    if (montantCompte === '') { showCaisseToast('error', 'Veuillez saisir le montant compté'); return; }
    setSubmittingCaisse(true);
    try {
      await fermerCaisse({ boutiqueId: caisseModal.boutique._id, montantCompteCaisse: parseFloat(montantCompte) || 0, noteFermeture: noteCaisse });
      showCaisseToast('success', `Caisse fermée — ${caisseModal.boutique.nom}`);
      setCaisseModal(null); setMontantCompte(''); setNoteCaisse('');
      await loadCaisses();
    } catch (err) {
      showCaisseToast('error', err.response?.data?.message || err.message || 'Erreur lors de la fermeture');
    } finally { setSubmittingCaisse(false); }
  };

  /* ── derived ── */
  const caTotal  = evolution.reduce((s, d) => s + (d.chiffreAffaires || 0), 0);
  const nbAlerts = alertes.length + (kpiStats?.stock?.produitsRupture || 0);

  const trendPct = (() => {
    if (evolution.length < 2) return { val: 0, up: true };
    const last = evolution[evolution.length - 1]?.chiffreAffaires || 0;
    const prev = evolution[evolution.length - 2]?.chiffreAffaires || 0;
    if (!prev) return { val: 0, up: true };
    const p = ((last - prev) / prev) * 100;
    return { val: Math.abs(p).toFixed(1), up: p >= 0 };
  })();

  const boutiquesParCA = (dashStats?.ventesParBoutique || [])
    .map(b => ({ name: b.boutique || 'Boutique', value: b.total || 0 }))
    .sort((a, b) => b.value - a.value);

  /* ── KPI cards ── */
  const kpis = [
    {
      label: 'Boutiques actives',
      value: fmtNum(dashStats?.boutiques || boutiques.filter(b => b.actif !== false).length),
      sub: 'points de vente',
      icon: Store,
      color: '#6366f1',
      bg: '#eef2ff',
      trend: null
    },
    {
      label: "CA aujourd'hui",
      value: fmt(dashStats?.ventesJour?.total || kpiStats?.ventesJour?.chiffreAffaires),
      sub: (() => {
        const n = dashStats?.ventesJour?.nombre || kpiStats?.ventesJour?.nombre;
        const ac = dashStats?.ventesJour?.acomptes ?? kpiStats?.ventesJour?.acomptes ?? 0;
        return `${fmtNum(n)} ventes${ac > 0 ? ` · dont ${fmt(ac)} acomptes` : ''}`;
      })(),
      icon: DollarSign,
      color: '#fca311',
      bg: '#fff8ec',
      trend: { val: `${trendPct.up ? '+' : '-'}${trendPct.val}%`, up: trendPct.up }
    },
    {
      label: 'CA ce mois',
      value: fmt(dashStats?.ventesMois?.total || kpiStats?.ventesMois?.chiffreAffaires),
      sub: `${fmtNum(dashStats?.ventesMois?.nombre || kpiStats?.ventesMois?.nombre)} transactions`,
      icon: TrendingUp,
      color: '#10b981',
      bg: '#ecfdf5',
      trend: null
    },
    {
      label: 'Alertes stock',
      value: fmtNum(typeof dashStats?.produitsAlerte === 'number'
        ? dashStats.produitsAlerte
        : dashStats?.produitsAlerte?.nombre || kpiStats?.stock?.produitsRupture || 0),
      sub: 'produits sous seuil',
      icon: AlertTriangle,
      color: nbAlerts > 0 ? '#ef4444' : '#10b981',
      bg: nbAlerts > 0 ? '#fef2f2' : '#ecfdf5',
      trend: null
    }
  ];

  /* ── loading ── */
  if (loading) return (
    <div className="de-loading">
      <Loader className="de-spinner" size={32} />
      <span>Chargement du tableau de bord…</span>
    </div>
  );

  return (
    <div className="de-page">

      {/* ══ Page Header ══ */}
      <div className="de-header">
        <div className="de-header-left">
          {config.logo && (
            <img src={config.logo} alt="logo" className="de-logo"
              onError={e => { e.target.style.display = 'none'; }} />
          )}
          <div>
            <p className="de-header-date">{moment().format('dddd D MMMM YYYY')}</p>
            <h1 className="de-header-title">
              {config.nom_entreprise || 'Tableau de bord entreprise'}
            </h1>
            <p className="de-header-sub">Vue globale de toutes vos boutiques</p>
          </div>
        </div>

        <div className="de-header-right">
          <div className="de-range-tabs">
            {[['7','7j'],['30','30j'],['90','90j']].map(([v,l]) => (
              <button key={v}
                className={`de-range-tab ${timeRange === v ? 'active' : ''}`}
                onClick={() => setTimeRange(v)}
              >{l}</button>
            ))}
          </div>
          <button className="de-icon-btn" onClick={() => load(true)} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'de-spin' : ''} />
          </button>
          <button className="de-cta-btn" onClick={() => navigate('/ventes/nouvelle')}>
            <Plus size={15} /> Nouvelle vente
          </button>
        </div>
      </div>

      {/* ══ Tabs ══ */}
      <div className="de-tabs">
        {[
          ['overview',  'Vue d\'ensemble'],
          ['boutiques', `Boutiques (${boutiques.length})`],
          ['caisses',   `Caisses (${boutiques.length})`],
          ['rapports',  'Rapports'],
          ['alertes',   `Alertes (${alertes.length})`],
        ].map(([k, l]) => (
          <button key={k}
            className={`de-tab ${tab === k ? 'active' : ''}`}
            onClick={() => setTab(k)}
          >{l}</button>
        ))}
      </div>

      {/* ══ Overview Tab ══ */}
      {tab === 'overview' && (
        <div className="de-content">

          {/* KPI row */}
          <div className="de-kpi-row">
            {kpis.map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={i} className="de-kpi" style={{ borderTopColor: k.color }}>
                  <div className="de-kpi-top">
                    <div className="de-kpi-icon" style={{ background: k.bg, color: k.color }}>
                      <Icon size={19} />
                    </div>
                    {k.trend && (
                      <span className={`de-trend ${k.trend.up ? 'up' : 'down'}`}>
                        {k.trend.up ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
                        {k.trend.val}
                      </span>
                    )}
                  </div>
                  <div className="de-kpi-val">{k.value}</div>
                  <div className="de-kpi-label">{k.label}</div>
                  <div className="de-kpi-sub">{k.sub}</div>
                </div>
              );
            })}
          </div>

          {/* ══ Fidélité ══ */}
          {loyaltyEnabled && loyaltyOverview && (
            <div className="de-card" style={{ marginBottom: 16 }}>
              <div className="de-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                  <Award size={16} color="#6366f1"/> Fidélité
                </h3>
                <Link to="/loyalty/cartes" className="de-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Voir les cartes <ChevronRight size={14}/>
                </Link>
              </div>
              <div className="de-kpi-row" style={{ marginTop: 12 }}>
                {[
                  { label: 'Cartes', value: fmtNum(loyaltyOverview.cartes), icon: CreditCard, color: '#6366f1', bg: '#eef2ff' },
                  { label: 'Points en circulation', value: fmtNum(loyaltyOverview.soldeTotal), icon: TrendingUp, color: '#10b981', bg: '#ecfdf5' },
                  { label: 'Bons actifs', value: fmtNum(loyaltyOverview.coupons?.active), icon: Ticket, color: '#f59e0b', bg: '#fff8ec' },
                  { label: 'Bons utilisés', value: fmtNum(loyaltyOverview.coupons?.used), icon: Gift, color: '#0ea5e9', bg: '#e0f2fe' },
                ].map((k, i) => {
                  const Icon = k.icon;
                  return (
                    <div key={i} className="de-kpi" style={{ borderTopColor: k.color }}>
                      <div className="de-kpi-top">
                        <div className="de-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={18}/></div>
                      </div>
                      <div className="de-kpi-val">{k.value}</div>
                      <div className="de-kpi-label">{k.label}</div>
                    </div>
                  );
                })}
              </div>
              {loyaltyOverview.top?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>Top clients fidèles</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {loyaltyOverview.top.map((t, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12,
                      }}>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{t.nom}</span>
                        <span style={{ color: '#6366f1', fontWeight: 700 }}>{fmtNum(t.balance)} pts</span>
                        <span style={{ color: '#94a3b8' }}>{t.tierLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ Produits vendus aujourd'hui ══ */}
          <div className="de-card" style={{ marginBottom: 16 }}>
            <div className="de-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div className="de-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Package size={16} color="#6366f1"/> Produits vendus {pvjDate === moment().format('YYYY-MM-DD') ? "aujourd'hui" : `le ${moment(pvjDate).format('DD/MM/YYYY')}`}
                </div>
                <div className="de-card-sub">
                  {produitsJour
                    ? `${fmtNum(produitsJour.nombreProduits)} référence(s) · ${fmtNum(produitsJour.totalQuantite)} unité(s) · ${fmt(produitsJour.totalCA)}`
                    : '—'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input type="date" value={pvjDate}
                  max={moment().format('YYYY-MM-DD')}
                  onChange={e => { setPvjDate(e.target.value); setPvjShowAll(false); loadProduitsJour(e.target.value); }}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}/>
                <button type="button" onClick={exportProduitsJourExcel}
                  disabled={!produitsJour?.liste?.length}
                  title="Exporter tous les produits vendus au format Excel"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    borderRadius: 8, border: '1px solid #10b981', fontSize: 13, fontWeight: 600,
                    background: produitsJour?.liste?.length ? '#10b981' : '#e2e8f0',
                    borderColor: produitsJour?.liste?.length ? '#10b981' : '#e2e8f0',
                    color: produitsJour?.liste?.length ? '#fff' : '#94a3b8',
                    cursor: produitsJour?.liste?.length ? 'pointer' : 'not-allowed',
                  }}>
                  <Download size={14}/> Excel
                </button>
                <Link to="/ventes" className="de-see-all">Voir les ventes <ChevronRight size={13}/></Link>
              </div>
            </div>

            {!produitsJour || produitsJour.liste.length === 0 ? (
              <div className="de-empty"><Package size={30}/><p>Aucun produit vendu {pvjDate === moment().format('YYYY-MM-DD') ? "aujourd'hui" : 'ce jour-là'}</p></div>
            ) : (
              <div className="de-top-table" style={{ marginTop: 10 }}>
                <div className="de-top-row" style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                  <span style={{ flex: 1 }}>Produit</span>
                  <span style={{ width: 70, textAlign: 'right' }}>Qté</span>
                  <span style={{ width: 110, textAlign: 'right' }}>CA</span>
                </div>
                <div style={pvjShowAll ? { maxHeight: 420, overflowY: 'auto' } : undefined}>
                  {(pvjShowAll ? produitsJour.liste : produitsJour.liste.slice(0, 12)).map((p, i) => (
                    <div key={p.produitId || i} className="de-top-row" style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.nom}</span>
                        {p.codeProduit && <span style={{ color: '#94a3b8', fontSize: 11 }}> · {p.codeProduit}</span>}
                      </span>
                      <strong style={{ width: 70, textAlign: 'right', color: '#6366f1' }}>{fmtNum(p.quantiteVendue)}</strong>
                      <span style={{ width: 110, textAlign: 'right', fontWeight: 600 }}>{fmt(p.chiffreAffaires)}</span>
                    </div>
                  ))}
                </div>

                <div className="de-top-row" style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #e2e8f0', fontWeight: 700 }}>
                  <span style={{ flex: 1, minWidth: 0, color: '#0f172a' }}>
                    Total · {fmtNum(produitsJour.nombreProduits)} référence(s)
                  </span>
                  <strong style={{ width: 70, textAlign: 'right', color: '#6366f1' }}>{fmtNum(produitsJour.totalQuantite)}</strong>
                  <span style={{ width: 110, textAlign: 'right' }}>{fmt(produitsJour.totalCA)}</span>
                </div>

                {produitsJour.liste.length > 12 && (
                  <div style={{ textAlign: 'center', paddingTop: 8 }}>
                    <button type="button" onClick={() => setPvjShowAll(v => !v)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                        fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer',
                      }}>
                      {pvjShowAll
                        ? <>Réduire la liste <ChevronUp size={13}/></>
                        : <>Afficher les {produitsJour.liste.length - 12} autre(s) produit(s) <ChevronDown size={13}/></>}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main chart + pie */}
          <div className="de-row-2">

            {/* Area chart */}
            <div className="de-card de-card--chart">
              <div className="de-card-head">
                <div>
                  <div className="de-card-title">Évolution du chiffre d'affaires</div>
                  <div className="de-card-sub">
                    {timeRange} derniers jours ·&nbsp;
                    <strong>{fmt(caTotal)}</strong>
                  </div>
                </div>
                <div className="de-legend">
                  <span className="de-legend-dot" style={{ background: '#fca311' }}/>
                  CA journalier
                </div>
              </div>

              {evolution.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={evolution} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="deGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#fca311" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#fca311" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                    <XAxis dataKey="date" tickFormatter={d => moment(d).format('DD/MM')}
                      tick={{ fontSize:11, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
                    <YAxis tickFormatter={fmtK}
                      tick={{ fontSize:11, fill:'#94a3b8' }} axisLine={false} tickLine={false} width={46}/>
                    <Tooltip
                      formatter={v => [fmt(v),'CA']}
                      labelFormatter={d => moment(d).format('ddd D MMM')}
                      contentStyle={{ background:'#0f172a', color:'white', border:'none',
                        borderRadius:'8px', fontSize:'12px', padding:'10px 14px' }}
                      labelStyle={{ color:'#94a3b8', marginBottom:'4px' }}
                    />
                    <Area type="monotone" dataKey="chiffreAffaires"
                      stroke="#fca311" strokeWidth={2.5} fill="url(#deGrad)"
                      dot={false} activeDot={{ r:5, fill:'#fca311', strokeWidth:2, stroke:'white' }}/>
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="de-empty">
                  <BarChart3 size={36}/>
                  <p>Aucune donnée sur {timeRange} jours</p>
                </div>
              )}
            </div>

            {/* Répartition CA par boutique */}
            <div className="de-card de-card--pie">
              <div className="de-card-head">
                <div className="de-card-title">Répartition par boutique</div>
                <div className="de-card-sub">CA ce mois</div>
              </div>

              {boutiquesParCA.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={boutiquesParCA}
                        cx="50%" cy="50%"
                        innerRadius={52} outerRadius={78}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {boutiquesParCA.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={v => [fmt(v),'CA']}
                        contentStyle={{ background:'#0f172a', color:'white', border:'none',
                          borderRadius:'8px', fontSize:'12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="de-pie-legend">
                    {boutiquesParCA.slice(0,5).map((b, i) => (
                      <div key={i} className="de-pie-item">
                        <span className="de-pie-dot" style={{ background: PALETTE[i % PALETTE.length] }}/>
                        <span className="de-pie-name">{b.name}</span>
                        <span className="de-pie-val">{fmt(b.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="de-empty">
                  <Store size={32}/>
                  <p>Aucune donnée boutique</p>
                </div>
              )}
            </div>
          </div>

          {/* Top produits + quick links */}
          <div className="de-row-3">

            {/* Top produits */}
            <div className="de-card">
              <div className="de-card-head">
                <div>
                  <div className="de-card-title">Top produits</div>
                  <div className="de-card-sub">Les plus vendus sur {timeRange} jours</div>
                </div>
                <Link to="/produits" className="de-see-all">
                  Voir tout <ChevronRight size={13}/>
                </Link>
              </div>

              {topProduits.length === 0 ? (
                <div className="de-empty"><Package size={30}/><p>Aucune donnée</p></div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart
                      data={topProduits.slice(0,6).map(p => ({
                        name: (p.produit?.nom || '').substring(0,12),
                        ca: p.chiffreAffaires
                      }))}
                      margin={{ top:4, right:4, bottom:0, left:0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                      <XAxis dataKey="name" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
                      <YAxis tickFormatter={fmtK} tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} width={40}/>
                      <Tooltip
                        formatter={v => [fmt(v),'CA']}
                        contentStyle={{ background:'#0f172a', color:'white', border:'none', borderRadius:'8px', fontSize:'12px' }}
                      />
                      <Bar dataKey="ca" radius={[5,5,0,0]}>
                        {topProduits.slice(0,6).map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="de-top-table">
                    {topProduits.slice(0,5).map((p, i) => (
                      <div key={i} className="de-top-row">
                        <span className="de-top-rank"
                          style={{ background: PALETTE[i%PALETTE.length]+'22', color: PALETTE[i%PALETTE.length] }}>
                          #{i+1}
                        </span>
                        <span className="de-top-name">{p.produit?.nom || '—'}</span>
                        <span className="de-top-qty">{p.quantiteVendue} unités</span>
                        <span className="de-top-ca">{fmt(p.chiffreAffaires)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Quick access */}
            <div className="de-card de-card--actions">
              <div className="de-card-head">
                <div className="de-card-title">Accès rapide</div>
              </div>

              <div className="de-quick-grid">
                {[
                  { label:'Nouvelle vente',     icon:ShoppingCart, color:'#fca311', bg:'#fff8ec', to:'/ventes/nouvelle' },
                  { label:'Caisse du jour',      icon:Landmark,     color:'#6366f1', bg:'#eef2ff', to:'/caisse' },
                  { label:'Gérer produits',      icon:Package,      color:'#10b981', bg:'#ecfdf5', to:'/produits' },
                  { label:'Voir les ventes',     icon:TrendingUp,   color:'#0ea5e9', bg:'#f0f9ff', to:'/ventes' },
                  { label:'Gérer boutiques',     icon:Store,        color:'#8b5cf6', bg:'#f5f3ff', to:'/boutiques' },
                  { label:'Utilisateurs',        icon:Users,        color:'#f43f5e', bg:'#fff1f2', to:'/utilisateurs' },
                ].map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <button key={i} className="de-quick-btn" onClick={() => navigate(a.to)}>
                      <div className="de-quick-icon" style={{ background: a.bg, color: a.color }}>
                        <Icon size={18}/>
                      </div>
                      <span>{a.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Alertes mini */}
              {alertes.length > 0 && (
                <div className="de-alert-mini">
                  <div className="de-alert-mini-title">
                    <AlertTriangle size={14} color="#ef4444"/>
                    {alertes.length} alerte{alertes.length > 1 ? 's' : ''} en cours
                  </div>
                  {alertes.slice(0,2).map((a, i) => (
                    <div key={i} className={`de-alert-chip de-alert-${a.type}`}>
                      {a.titre}
                    </div>
                  ))}
                  <button className="de-see-all" style={{ marginTop:8 }}
                    onClick={() => setTab('alertes')}>
                    Voir toutes <ChevronRight size={12}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ Boutiques Tab ══ */}
      {tab === 'boutiques' && (
        <div className="de-content">
          <div className="de-boutiques-header">
            <p className="de-section-sub">{boutiques.length} boutique{boutiques.length !== 1 ? 's' : ''} enregistrée{boutiques.length !== 1 ? 's' : ''}</p>
            <Link to="/boutiques" className="de-cta-btn de-cta-btn--outline">
              <Store size={14}/> Gérer les boutiques
            </Link>
          </div>

          {boutiques.length === 0 ? (
            <div className="de-empty-big">
              <Store size={48}/>
              <h3>Aucune boutique créée</h3>
              <p>Commencez par créer votre première boutique</p>
              <Link to="/boutiques" className="de-cta-btn">
                <Plus size={14}/> Créer une boutique
              </Link>
            </div>
          ) : (
            <div className="de-boutiques-grid">
              {boutiques.map((b, i) => {
                const bStats = dashStats?.ventesParBoutique?.find(v =>
                  v.boutiqueId?.toString() === b._id?.toString()
                );
                const color = PALETTE[i % PALETTE.length];
                return (
                  <div key={b._id} className="de-boutique-card" style={{ borderTopColor: color }}>
                    <div className="de-boutique-head">
                      <div className="de-boutique-icon" style={{ background: color + '18', color }}>
                        <Store size={20}/>
                      </div>
                      <div className="de-boutique-info">
                        <div className="de-boutique-name">{b.nom}</div>
                        <div className="de-boutique-addr">{b.adresse || 'Adresse non renseignée'}</div>
                      </div>
                      {b.principale && (
                        <span className="de-badge-principale">Principale</span>
                      )}
                      {b.actif === false && (
                        <span className="de-badge-inactive">Inactive</span>
                      )}
                    </div>

                    <div className="de-boutique-stats">
                      <div className="de-bstat">
                        <span className="de-bstat-val">{fmtNum(b.nbProduits || 0)}</span>
                        <span className="de-bstat-label">Produits</span>
                      </div>
                      <div className="de-bstat">
                        <span className="de-bstat-val" style={{ color: '#fca311' }}>
                          {fmt(bStats?.total || b.ventesJour || 0)}
                        </span>
                        <span className="de-bstat-label">CA du jour</span>
                      </div>
                      <div className="de-bstat">
                        <span className="de-bstat-val">{fmt(b.ventesMois || 0)}</span>
                        <span className="de-bstat-label">CA mois</span>
                      </div>
                      <div className="de-bstat">
                        <span className="de-bstat-val"
                          style={{ color: (b.produitsAlerte || 0) > 0 ? '#ef4444' : '#10b981' }}>
                          {b.produitsAlerte || 0}
                        </span>
                        <span className="de-bstat-label">Alertes</span>
                      </div>
                    </div>

                    <div className="de-boutique-footer">
                      <Link to={`/ventes?boutique=${b._id}`} className="de-boutique-link">
                        <ShoppingCart size={13}/> Ventes
                      </Link>
                      <Link to={`/stock?boutique=${b._id}`} className="de-boutique-link">
                        <Package size={13}/> Stock
                      </Link>
                      <Link to={`/caisse?boutique=${b._id}`} className="de-boutique-link">
                        <Landmark size={13}/> Caisse
                      </Link>
                      <Link to="/boutiques" className="de-boutique-link de-boutique-link--icon">
                        <ExternalLink size={13}/>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ Caisses Tab ══ */}
      {tab === 'caisses' && (
        <div className="de-content">
          <div className="de-caisses-header">
            <p className="de-section-sub">Gérez l'ouverture et la fermeture des caisses par boutique</p>
            <button className="de-icon-btn" onClick={loadCaisses} disabled={loadingCaisses} title="Actualiser">
              <RefreshCw size={15} className={loadingCaisses ? 'de-spin' : ''}/>
            </button>
          </div>

          {loadingCaisses ? (
            <div className="de-loading" style={{ minHeight: 200 }}>
              <Loader className="de-spinner" size={24}/> <span>Chargement des caisses…</span>
            </div>
          ) : (
            <div className="de-caisses-grid">
              {boutiques.map((b, i) => {
                const caisse   = caisses[b._id];
                const ouverte  = caisse !== null && caisse !== undefined && caisse?.statut === 'ouverte';
                const color    = PALETTE[i % PALETTE.length];
                const fmtC     = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' ' + (config.devise || 'XOF');
                return (
                  <div key={b._id} className={`de-caisse-card ${ouverte ? 'de-caisse-card--open' : 'de-caisse-card--closed'}`}>
                    <div className="de-caisse-card-head">
                      <div className="de-caisse-boutique-icon" style={{ background: color + '18', color }}>
                        <Store size={16}/>
                      </div>
                      <div className="de-caisse-boutique-info">
                        <div className="de-caisse-boutique-name">{b.nom}</div>
                        <div className="de-caisse-boutique-addr">{b.adresse || ''}</div>
                      </div>
                      <span className={`de-caisse-statut ${ouverte ? 'de-caisse-statut--open' : 'de-caisse-statut--closed'}`}>
                        {ouverte ? <><Unlock size={11}/> Ouverte</> : <><Lock size={11}/> Fermée</>}
                      </span>
                    </div>

                    {ouverte && caisse ? (
                      <div className="de-caisse-infos">
                        <div className="de-caisse-info-row">
                          <span>Fond de caisse</span>
                          <strong>{fmtC(caisse.fondCaisse)}</strong>
                        </div>
                        <div className="de-caisse-info-row">
                          <span>Ventes espèces</span>
                          <strong>{fmtC(caisse.totalEspeces)}</strong>
                        </div>
                        <div className="de-caisse-info-row">
                          <span>Espèces attendues</span>
                          <strong style={{ color: '#6366f1' }}>{fmtC(caisse.especesAttendues)}</strong>
                        </div>
                        {caisse.caissier && (
                          <div className="de-caisse-info-row">
                            <span>Caissier</span>
                            <strong>{caisse.caissier.prenom} {caisse.caissier.nom}</strong>
                          </div>
                        )}
                        <div className="de-caisse-info-row">
                          <span>Ouverture</span>
                          <strong>{caisse.dateOuverture ? new Date(caisse.dateOuverture).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }) : '—'}</strong>
                        </div>
                      </div>
                    ) : (
                      <div className="de-caisse-closed-msg">
                        <Lock size={20}/>
                        <span>Aucune caisse ouverte</span>
                      </div>
                    )}

                    <div className="de-caisse-actions">
                      {ouverte ? (
                        <button
                          className="de-caisse-btn de-caisse-btn--fermer"
                          onClick={() => { setCaisseModal({ type:'fermer', boutique: b }); setMontantCompte(''); setNoteCaisse(''); }}>
                          <Lock size={13}/> Fermer la caisse
                        </button>
                      ) : (
                        <button
                          className="de-caisse-btn de-caisse-btn--ouvrir"
                          onClick={() => { setCaisseModal({ type:'ouvrir', boutique: b }); setFondCaisse(''); setNoteCaisse(''); }}>
                          <Unlock size={13}/> Ouvrir la caisse
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ Rapports Tab ══ */}
      {tab === 'rapports' && (
        <div className="de-content">
          {/* Filtres */}
          <div className="de-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Boutique</label>
                <select value={rapBoutique} onChange={e => setRapBoutique(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 180 }}>
                  <option value="all">Toutes les boutiques</option>
                  {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Du</label>
                <input type="date" value={rapFrom} onChange={e => setRapFrom(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}/>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Au</label>
                <input type="date" value={rapTo} onChange={e => setRapTo(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}/>
              </div>
              <button className="de-cta-btn" onClick={loadRapport} disabled={rapLoading}>
                {rapLoading ? <Loader size={14} className="de-spin"/> : 'Générer'}
              </button>
            </div>
          </div>

          {rapLoading ? (
            <div className="de-empty-big"><Loader size={32} className="de-spin"/><p>Calcul en cours…</p></div>
          ) : !rapData ? (
            <div className="de-empty-big"><BarChart3 size={42} color="#94a3b8"/><p>Choisissez une boutique et une période, puis « Générer ».</p></div>
          ) : (
            <>
              {/* KPIs */}
              <div className="de-kpi-row">
                {[
                  { label: 'Ventes (CA)', value: rapData.ventes.total, color: '#16a34a', bg: '#dcfce7', icon: TrendingUp, sub: `${fmtNum(rapData.ventes.nombre)} ventes` },
                  { label: 'Dépenses',    value: rapData.depenses.total, color: '#dc2626', bg: '#fef2f2', icon: ArrowDownRight, sub: 'Sorties + achats' },
                  { label: 'Net',         value: rapData.net, color: rapData.net >= 0 ? '#0891b2' : '#dc2626', bg: rapData.net >= 0 ? '#ecfeff' : '#fef2f2', icon: DollarSign, sub: 'Ventes − dépenses' },
                ].map((k, i) => {
                  const Icon = k.icon;
                  return (
                    <div key={i} className="de-kpi" style={{ borderTopColor: k.color }}>
                      <div className="de-kpi-top"><div className="de-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={18}/></div></div>
                      <div className="de-kpi-val">{fmt(k.value)}</div>
                      <div className="de-kpi-label">{k.label}</div>
                      <div className="de-kpi-sub">{k.sub}</div>
                    </div>
                  );
                })}
              </div>

              {/* Détail dépenses */}
              <div className="de-row-2" style={{ marginTop: 16 }}>
                <div className="de-card">
                  <div className="de-card-head"><h3 style={{ margin: 0 }}>Détail des dépenses</h3></div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span>Sorties de caisse</span><strong>{fmt(rapData.depenses.sortiesCaisse)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span>Achats fournisseurs <small style={{ color: '#94a3b8' }}>(toutes boutiques)</small></span>
                      <strong>{fmt(rapData.depenses.achatsFournisseurs)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, color: '#dc2626' }}>
                      <span>Total dépenses</span><strong>{fmt(rapData.depenses.total)}</strong>
                    </div>
                  </div>
                </div>

                <div className="de-card">
                  <div className="de-card-head"><h3 style={{ margin: 0 }}>Sorties de caisse par motif</h3></div>
                  <div style={{ marginTop: 10, maxHeight: 240, overflow: 'auto' }}>
                    {rapData.sortiesParMotif.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: 13 }}>Aucune sortie de caisse sur la période.</p>
                    ) : rapData.sortiesParMotif.map((s, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                        <span>{s.motif}</span><strong>{fmt(s.total)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ Alertes Tab ══ */}
      {tab === 'alertes' && (
        <div className="de-content">
          {alertes.length === 0 ? (
            <div className="de-empty-big">
              <AlertTriangle size={48} color="#10b981"/>
              <h3>Aucune alerte</h3>
              <p>Tout va bien ! Aucune alerte système en cours.</p>
            </div>
          ) : (
            <div className="de-alertes-list">
              {alertes.map((a, i) => {
                const icons = { stock: Package, abonnement: DollarSign, utilisateur: Users };
                const colors = { stock: '#ef4444', abonnement: '#f59e0b', utilisateur: '#6366f1' };
                const bgs    = { stock: '#fef2f2', abonnement: '#fffbeb', utilisateur: '#eef2ff' };
                const Icon = icons[a.type] || AlertTriangle;
                const color = colors[a.type] || '#64748b';
                const bg    = bgs[a.type] || '#f8fafc';
                return (
                  <div key={i} className="de-alerte-card">
                    <div className="de-alerte-icon" style={{ background: bg, color }}>
                      <Icon size={22}/>
                    </div>
                    <div className="de-alerte-body">
                      <div className="de-alerte-title">{a.titre}</div>
                      <div className="de-alerte-desc">{a.description}</div>
                    </div>
                    <span className={`de-alerte-type de-alerte-type--${a.type}`}>
                      {a.type === 'stock' ? 'Stock' : a.type === 'abonnement' ? 'Abonnement' : 'Utilisateur'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* ══ Toast caisse ══ */}
      {caissToast && (
        <div className={`de-caisse-toast de-caisse-toast--${caissToast.type}`}>
          {caissToast.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
          {caissToast.text}
        </div>
      )}

      {/* ══ Modal Ouvrir Caisse ══ */}
      {caisseModal?.type === 'ouvrir' && (
        <>
          <div className="de-modal-overlay" onClick={() => setCaisseModal(null)}/>
          <div className="de-modal">
            <div className="de-modal-head">
              <div>
                <h2 className="de-modal-title"><Unlock size={15}/> Ouvrir la caisse</h2>
                <p className="de-modal-sub">{caisseModal.boutique.nom}</p>
              </div>
              <button className="de-modal-close" onClick={() => setCaisseModal(null)}><X size={16}/></button>
            </div>
            <div className="de-modal-body">
              <div className="de-modal-field">
                <label>Fond de caisse (espèces en caisse à l'ouverture)</label>
                <input type="number" min="0" step="500" autoFocus
                  placeholder="0"
                  value={fondCaisse}
                  onChange={e => setFondCaisse(e.target.value)}/>
              </div>
              <div className="de-modal-field">
                <label>Note d'ouverture <span className="de-modal-opt">(optionnel)</span></label>
                <input type="text" placeholder="Remarque…"
                  value={noteCaisse}
                  onChange={e => setNoteCaisse(e.target.value)}/>
              </div>
            </div>
            <div className="de-modal-footer">
              <button className="de-modal-btn-cancel" onClick={() => setCaisseModal(null)}>Annuler</button>
              <button className="de-modal-btn-confirm de-modal-btn-confirm--open"
                onClick={handleOuvrirCaisse} disabled={submittingCaisse}>
                {submittingCaisse
                  ? <><Loader size={13} className="de-spin"/> Ouverture…</>
                  : <><Unlock size={13}/> Ouvrir</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══ Modal Fermer Caisse ══ */}
      {caisseModal?.type === 'fermer' && (
        <>
          <div className="de-modal-overlay" onClick={() => setCaisseModal(null)}/>
          <div className="de-modal">
            <div className="de-modal-head">
              <div>
                <h2 className="de-modal-title"><Lock size={15}/> Fermer la caisse</h2>
                <p className="de-modal-sub">{caisseModal.boutique.nom}</p>
              </div>
              <button className="de-modal-close" onClick={() => setCaisseModal(null)}><X size={16}/></button>
            </div>
            <div className="de-modal-body">
              <div className="de-modal-field">
                <label>Montant compté en caisse *</label>
                <input type="number" min="0" step="500" required autoFocus
                  placeholder="Montant physiquement présent"
                  value={montantCompte}
                  onChange={e => setMontantCompte(e.target.value)}/>
              </div>
              <div className="de-modal-field">
                <label>Note de fermeture <span className="de-modal-opt">(optionnel)</span></label>
                <input type="text" placeholder="Remarque…"
                  value={noteCaisse}
                  onChange={e => setNoteCaisse(e.target.value)}/>
              </div>
            </div>
            <div className="de-modal-footer">
              <button className="de-modal-btn-cancel" onClick={() => setCaisseModal(null)}>Annuler</button>
              <button className="de-modal-btn-confirm de-modal-btn-confirm--close"
                onClick={handleFermerCaisse} disabled={submittingCaisse}>
                {submittingCaisse
                  ? <><Loader size={13} className="de-spin"/> Fermeture…</>
                  : <><Lock size={13}/> Fermer la caisse</>}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
